// Microsoft Teams basics shared by the chat entry points (utils/teams/teamsChats.js) and the chat
// pane (utils/teams/teamsChat.js). Pure: no React, no store. Same rules as the mobile app
// (Kjuup-Mobile-App src/utils/teamsCore.js).

export const TEAMS_SOURCE = "teams";

/** Prefix of the synthetic ids of Teams people (`teams:<aadUserId>`); they are never Kjuup users. */
export const TEAMS_ID_PREFIX = "teams:";

/**
 * The backend's full v1 list (TeamsGuardService.UNSUPPORTED_ACTIONS, same order). Used when a payload
 * marks a chat as Teams but carries no list of its own (latestMsg, push, search, deep links).
 * 'task' is the room task; manual to-dos ("Mark as task"), translation and AI to-dos are allowed.
 */
export const TEAMS_UNSUPPORTED_ACTIONS = Object.freeze([
  "addMembers", "removeMember", "join", "leaveGroup", "deleteGroup", "clearChat", "assignAdmin",
  "rename", "changeDescription", "groupImage", "deeplink", "block", "poll", "location", "contact",
  "voiceMessage", "audioCall", "videoCall", "meeting", "forwardIn", "forwardOut", "task",
  "readReceipts",
]);

/** True when the object itself says it is Teams (`isTeams`, `source: 'teams'` or `chatSource: 'teams'`). */
export const hasTeamsMark = (o) =>
  !!o && typeof o === "object" && (o.isTeams === true || o.source === TEAMS_SOURCE || o.chatSource === TEAMS_SOURCE);

/** True for a group / row / payload that is a Teams mirror (itself or its nested `group`). */
export function isTeamsPayload(p) {
  if (!p || typeof p !== "object") return false;
  return hasTeamsMark(p) || hasTeamsMark(p.group);
}

/** True for a synthetic Teams person id ('teams:<aadUserId>'). */
export const isTeamsUserId = (id) => typeof id === "string" && id.startsWith(TEAMS_ID_PREFIX);

/**
 * Initials for a Teams person or chat with no photo ("Anna Berg" → "AB"). `empty` is returned for a
 * blank name. By code point, so a name starting with an emoji keeps it whole (no lone surrogate).
 */
export function teamsInitials(name, empty = "") {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return empty;
  const firstOf = (p) => Array.from(p)[0] || "";
  const last = parts.length > 1 ? firstOf(parts[parts.length - 1]) : "";
  return (firstOf(parts[0]) + last).toUpperCase();
}

/** Text of a language key with a fallback (`strings` from LanguageContext). */
export const teamsText = (strings, key, fallback) => (strings && strings[key]) || fallback;
